/**
 * Route → permission map for admin-app UI gating (issue #131, [B][Rbac-02]).
 *
 * This file does NOT fork the matrix. It reads `ROLE_PERMISSIONS` from
 * `rbac-matrix.ts` (the in-lock-step browser mirror of the canonical
 * server matrix) and derives a scope-agnostic "can this role reach this
 * surface" answer from it. The server (`requirePermission`, RBAC-01 /
 * #130 / #156) remains the authoritative check at the precise scope —
 * this is a UX gate so a user never sees a nav item or lands on a route
 * the server will then 403.
 *
 * Why scope-agnostic for UI gating
 * --------------------------------
 * `roleHasPermission` is an exact `resource:action:scope` string match.
 * That's correct for action-level gating (e.g. a "Refund" button needs
 * exactly `payments:refund:all`). It is the WRONG granularity for
 * deciding whether a whole *surface* is reachable: an owner holds
 * `dashboard:read:all` while a walker holds `dashboard:read:scoped` —
 * both can open the dashboard, just see different slices. A manager
 * holds `payments:read:partial`, a groomer `payments:read:self` — both
 * can open the Payments surface. Gating the surface on one literal
 * scope string would wrongly lock out roles that legitimately have the
 * same `resource:action` at a different scope.
 *
 * So a route is reachable if the role holds the route's
 * `resource:action` prefix at ANY scope (`:all` | `:scoped` | `:self` |
 * `:partial`). `platform_admin` bypasses (control-plane, ADR-2026-04-28).
 * Unknown roles fail closed (return false) — same posture as the matrix.
 *
 * Lock-step contract: this map references permission *prefixes* that
 * exist in `rbac-matrix.ts`. If the server matrix renames a resource,
 * update both in the same commit (same rule the matrix header states).
 */

import {
  PLATFORM_ADMIN_ROLE,
  ROLE_PERMISSIONS,
  type RoleSlug,
} from './rbac-matrix.js';

/**
 * The common case: a guarded route is reachable if the role holds ANY
 * permission whose `resource:action` matches one of these prefixes. A
 * list (not a single prefix) because some surfaces are a union — the
 * Bookings page shows daycare/boarding, walking and grooming, so any one
 * booking-read grant is enough to make the surface meaningful.
 *
 * Keep these prefixes as `resource:action` (no scope segment) — the
 * scope is intentionally ignored here (see file header).
 */
export type RouteAccessPrefixes = ReadonlyArray<string>;

/**
 * Role-EXCLUSIVE form (#290 leak fix). A few surfaces — the four persona
 * "My Work" home screens (`/walker`, `/kennel`, `/groomer`,
 * `/accountant`) — must be reachable ONLY by the single role that owns
 * them (plus `platform_admin`), NOT by every role that happens to share
 * the same broad `resource:action` grant.
 *
 * The scope-agnostic prefix model is wrong for these: an owner/manager
 * holds `bookings.walking:read:all` and a manager/accountant holds
 * `revenue:read:all`, so a pure-prefix `/walker` or `/accountant` rule
 * leaks the persona item into the desk-role menus. `exactRoles` pins the
 * surface to its owner. The `prefixes` are still carried (and still
 * required) so the gate can never WIDEN past the matrix — a role must be
 * both listed in `exactRoles` AND hold the underlying grant (the owning
 * role always does, so this is belt-and-suspenders, not a second hurdle).
 *
 * `platform_admin` bypasses `exactRoles` exactly as it bypasses every
 * other gate (control-plane, ADR-2026-04-28).
 */
export type RouteAccessExactRolesRule = {
  readonly prefixes: ReadonlyArray<string>;
  readonly exactRoles: ReadonlyArray<RoleSlug>;
};

/**
 * A route access rule is EITHER a bare prefix list (scope-agnostic, the
 * default for almost every surface) OR a role-exclusive object that also
 * pins the owning role(s). Both flow through `roleCanAccess` /
 * `roleCanAccessPath` and the `TENANT_NAV` filter unchanged at every call
 * site — the object form just adds the `exactRoles` membership check.
 */
export type RouteAccessRule = RouteAccessPrefixes | RouteAccessExactRolesRule;

/**
 * Type guard: is this rule the role-exclusive object form? A plain rule
 * is an array; the exclusive form is an object with `exactRoles`.
 */
function isExactRolesRule(
  rule: RouteAccessRule,
): rule is RouteAccessExactRolesRule {
  return !Array.isArray(rule);
}

/**
 * Normalizes a rule to its underlying `resource:action` prefix list,
 * whichever form it takes. Used by the prefix-grant check in
 * `roleCanAccess`.
 */
function rulePrefixes(rule: RouteAccessRule): ReadonlyArray<string> {
  return isExactRolesRule(rule) ? rule.prefixes : rule;
}

/**
 * Canonical route → access-rule table. Keys are the admin-app route
 * paths from `App.tsx`. Sub-routes (`/customers/:id`) inherit their
 * parent's rule via `accessRuleForPath` prefix resolution.
 *
 * Mapping rationale (lowest permission that makes the surface usable):
 *  - `/`            Dashboard      → dashboard:read   (all 5 roles)
 *  - `/customers`   Customers      → customers:read   (owner/manager/recept)
 *  - `/bookings`    Bookings       → role-EXCLUSIVE to owner/mgr/recept (the
 *                                    facility-wide desk surface). walker/
 *                                    groomer/kennel_tech hold only a `:self`
 *                                    booking read on a single discipline and
 *                                    have their own scoped surfaces (/walker,
 *                                    /groomer, /my-schedule); the server's
 *                                    schedule/packages reads need the broad
 *                                    `:all`/`:scoped` grants, so a bare-prefix
 *                                    rule leaked the link to floor staff whose
 *                                    every fetch then 403'd (the raw
 *                                    "forbidden" bug). See the rule below.
 *  - `/loyalty`     Loyalty        → customers:read   (loyalty is a
 *                                    customer-reward surface; nav item
 *                                    added in the 2026-07-09 shell wave,
 *                                    Money section)
 *  - `/payments`    Payments       → payments-ops:read (owner/mgr/recept/
 *                                    accountant — the roles the SERVER
 *                                    grants the desk console to). #257
 *                                    review: the R1 gate was
 *                                    `payments:read`, which ALSO matched
 *                                    groomer's `payments:read:self` —
 *                                    but every console function
 *                                    (/api/v2/admin/payments/*) requires
 *                                    payments-ops:* / payments:refund:all,
 *                                    none of which groomer holds, so a
 *                                    groomer landed on an all-403 page.
 *                                    Groomer's `payments:*:self` grants
 *                                    are for its own commission/checkout
 *                                    surface (#133/#134 wave), not the
 *                                    desk console.
 *  - `/reports`     Reports        → revenue:read     (owner/manager) —
 *                                    matches server `requirePermission
 *                                    ('revenue:read:all')` on
 *                                    /api/admin/revenue
 *  - `/schedule`    Schedule       → staff-schedule:read (all 5 — owner/
 *                                    mgr/recept :all, walker/groomer
 *                                    :self; everyone can see their own
 *                                    schedule)
 *  - `/staff`       Staff          → rbac-admin:write  (owner only — the
 *                                    Staff page invites staff & assigns
 *                                    roles)
 *  - `/audit`       Audit log      → rbac-audit:read   (owner/manager) —
 *                                    matches server `requirePermission
 *                                    ('rbac-audit:read:all')`
 *
 * Platform routes (`/platform/*`) are intentionally absent: they are
 * gated by `PlatformAdminRoute` (404-shaped, anti-enumeration) and must
 * keep that distinct behavior.
 */
export const ROUTE_ACCESS_RULES = {
  '/': ['dashboard:read'],
  // Wave 5 (#940 Part A / #919) — the day list every rollup number opens.
  // Same gate as the dashboard: the SERVER's /rollup is DASHBOARD_READ,
  // which every role holds (all :all, floor roles :scoped) — so every role
  // that can read the dashboard can read the day it describes.
  '/calendar/day': ['dashboard:read'],
  // Wave 6 (#939 3b) — the Lodging Calendar: every bed, every stay, the
  // boarders with no bed, customer + dog names on every block. The SERVER's
  // /api/v2/admin/calendar/lodging carries the same desk-trio gate as the
  // calendar feed (booking :read:all / :read:scoped), so the page is pinned
  // to exactly owner / manager / receptionist — kennel_tech holds only
  // bookings.daycare-boarding:read:self, which the server 403s (#684 class).
  '/calendar/lodging': {
    prefixes: ['bookings.daycare-boarding:read'],
    exactRoles: ['owner', 'manager', 'receptionist'],
  },
  '/customers': ['customers:read'],
  // #553 — the dedicated Pet Profile page (`/dogs/:dogId`). A deep-link
  // surface (no nav item): reached from the customer record's dog cards, the
  // Dog ID Card modal's "Dog profile →", and search (later). Gated on the
  // scope-agnostic `pets:read` prefix, mirroring the SERVER's pets:read:all
  // check on GET /api/admin/dogs/:dogId/profile. Front-desk/owner/manager
  // (pets:read:all) are the audience; walker/groomer/kennel_tech hold only
  // pets:read:self, so the server 403s them — the page renders its honest
  // error state (not a blank), acceptable for v1 (specialist read is a
  // future server-map change). Not the exactRoles form: this is a normal
  // deep-linked surface, not a persona home screen.
  '/dogs': ['pets:read'],
  // Sawyer 08 meds-feeding (A1, G10) — the day's meds & feeding list.
  // Scope-agnostic `pets:read` admits exactly the six care roles (desk at
  // :all, floor at :self), matching the server's anyOf on GET /care/day;
  // the accountant holds no pets read. Deep-link surface (no nav item):
  // reached from the Day list / Dashboard buttons, Reports and the med pass.
  '/meds-feeding': ['pets:read'],
  // Sawyer 08 meds-feeding (A2) — the tablet med pass. Recording a dose is
  // the #649 append-only class (`pets.notes:write`, the six care roles,
  // never the accountant), matching POST /care/doses on the server.
  '/med-pass': ['pets.notes:write'],
  // The full Bookings page is a FACILITY-WIDE DESK surface: the all-services
  // schedule, Print Run Cards for the day's boarders, check-in/out/cancel, and
  // the money-path (package redeem + settle) across every service. Its data
  // fetches (`GET /api/bookings/admin/schedule` + `/service-types`,
  // `GET /api/v2/admin/payments/packages`) require the BROAD booking reads
  // (`:all`/`:scoped`) + `payments-ops:read:all`, which only owner/manager/
  // receptionist hold — so it is role-EXCLUSIVE to that desk trio.
  //
  // A bare scope-agnostic prefix rule was WRONG here (the #290 leak class):
  // walker (`bookings.walking:read:self`), groomer (`bookings.grooming:read:self`)
  // and kennel_tech (`bookings.daycare-boarding:read:self`) all match one of
  // these prefixes at `:self`, so the old `['bookings.daycare-boarding:read',
  // 'bookings.walking:read', 'bookings.grooming:read']` rule RENDERED the nav
  // link for them — then every data fetch on the page 403'd (the raw
  // "forbidden" box a walker hit live 2026-07-16). Floor staff have their own
  // role-scoped surfaces (/walker "My Walks", /groomer "My Day", /my-schedule);
  // they never need — and the server never grants them — the facility-wide desk
  // view (which carries all customers' PII + money). `exactRoles` pins the
  // surface to the desk trio; the `prefixes` are still required (defense in
  // depth, never widens past the matrix — each desk role holds a broad booking
  // read). Same fix shape as /schedule (#427 W2). The no-all-403 invariant in
  // route-permissions.test.ts locks this to the SERVER's real gate.
  '/bookings': {
    prefixes: [
      'bookings.daycare-boarding:read',
      'bookings.walking:read',
      'bookings.grooming:read',
    ],
    exactRoles: ['owner', 'manager', 'receptionist'],
  },
  // #548 — Front-Desk simple mode: the one-tap check-in surface for daily
  // floor staff (Kylie's first screen). Its core action is the check-in
  // POST (/api/bookings/admin/:id/check-in), which the SERVER gates on
  // bookings.daycare-boarding:write:all OR bookings.grooming:write:all.
  // Gating the nav on `bookings.daycare-boarding:write` selects EXACTLY
  // owner/manager/receptionist (the desk trio) — walker holds only
  // bookings.walking:write:self, groomer bookings.grooming:write:self, and
  // kennel_tech only bookings.daycare-boarding:READ:self, so none match the
  // daycare-boarding:WRITE prefix. This is the #484 lesson: the nav gate is
  // the exact permission the page's primary action consumes, so no role
  // sees an item whose check-in the server then 403s. The page's other
  // reads (dashboard:read /today, customers:read for lookup) are held by
  // all three admitted roles too — no all-403 surface.
  '/front-desk': ['bookings.daycare-boarding:write'],
  '/loyalty': ['customers:read'],
  // #814 — Pricing rules management. Gated on the page's PRIMARY action
  // (the #484 lesson): creating/editing rules is
  // `business-config.pricing:write:all` — owner-only per the matrix. A
  // `business-config:read` gate would also admit manager/receptionist,
  // whose every mutation the server then 403s (the #684 dead-affordance
  // class); they don't need a rules console to benefit from the rules.
  '/pricing-rules': ['business-config.pricing:write'],
  // #933 — Owner Settings (Operations now; Capacity and Services follow).
  // Gated on the scope-agnostic `business-config:read` prefix, which the
  // matrix grants to owner/manager/receptionist/accountant: the page is a
  // READ surface for the desk ("what is our cap today?") and a WRITE surface
  // for the owner and manager. Everyone else sees the values plus a plain
  // reason, never a dead Save (the PricingRules read-only pattern); the
  // server's PUT is business-config:write:all (owner, manager; #933 D2).
  '/settings': ['business-config:read'],
  // #257 — gated on payments-ops:read (NOT payments:read): the console's
  // every server route needs payments-ops:*/payments:refund:all, so the
  // gate must select exactly the roles with at least one working function
  // (owner/manager/receptionist full, accountant read-only). A
  // payments:read gate also matched groomer (:self) → all-403 page.
  '/payments': ['payments-ops:read'],
  // Incidents (#139) — reachable by exactly owner/manager/receptionist, mirroring
  // the SERVER enforcement (incidents:read:all — the same three roles). Gated on
  // `customers:write` (not `customers:read`): both select the desk trio, but only
  // `customers:write` ALSO excludes the read-only `accountant` (#135), which holds
  // `customers:read:all` for A/R yet has NO incident access — a `customers:read`
  // gate would show it an Incidents nav item the server then 403s. Deliberately NOT
  // ['incidents:read'], which would match walker/groomer/kennel_tech's
  // incidents:*:self prefix and expose a nav item the server 403s until their
  // scoped surfaces land (#133/#134).
  '/incidents': ['customers:write'],
  // Compliance roster (#109) — gated on customers:read so the surface is
  // reachable by exactly owner/manager/receptionist (the receptionists who
  // chase vax outreach), excluding walker/groomer. Same precedent as
  // /incidents. The SERVER grants every role (anyOf DASHBOARD_READ), so this
  // UI gate is strictly tighter than the server — no role sees a nav item
  // the server then 403s.
  '/compliance': ['customers:read'],
  // #551 — vaccination inbox. Gated on `pets:write`, matching the SERVER
  // gate on approve/reject (pets:write:all): the page's whole purpose is to
  // approve records onto a dog's file, so a role that can only READ the
  // queue would land on a screen whose only action 403s — a dead end (P2)
  // and a #484 violation in the other direction. Owner/manager/receptionist
  // hold it; walker/groomer/kennel_tech do not. The compliance roster
  // remains their read-only vax surface.
  '/vax-inbox': ['pets:write'],
  '/reports': ['revenue:read'],
  // #427 W3 — Timesheets & Payroll export. Reachable by exactly the roles
  // holding `payroll:read:all` (owner/manager/accountant), mirroring the
  // SERVER gate on every /api/v2/admin/timesheets + /api/v2/admin/payroll
  // route (payroll:read:all reads; approval + rate-write are tighter, gated
  // per-action inside the page). The scope-agnostic prefix `payroll:read`
  // admits only those three — no role holds `payroll:read:*` at another
  // scope — so no role sees an item the server then 403s. Lives in Money.
  '/timesheets': ['payroll:read'],
  // #427 W2 made /schedule planner-only; #665 P1 D1 (Johnny 2026-07-30)
  // opens it to the FLOOR roles too: walker/groomer/kennel_tech now hold
  // `staff-schedule:read:all` (the industry norm — every employee sees who's
  // working), the server shows them PUBLISHED shifts only (drafts stay
  // narrowed to write-role callers), and the #712 write-gate hides every
  // write affordance (Add shift / Copy last week / Publish / cell-add /
  // edit sheet), so the page is a clean read for them — no all-403 buttons,
  // which was the W2-era reason to keep them out. The ONE reader kept out
  // is the accountant: its `staff-schedule:read:self` against the facility
  // grid would render a near-empty week that lies about who's working — it
  // gets /my-schedule (below) instead.
  '/schedule': {
    prefixes: ['staff-schedule:read'],
    exactRoles: [
      'owner',
      'manager',
      'receptionist',
      'walker',
      'groomer',
      'kennel_tech',
    ],
  },
  // #427 W2 — /my-schedule (closes #400): a staffer's OWN week + their time
  // off. Role-EXCLUSIVE so it never becomes a redundant second schedule item
  // in the desk menus (owner/manager/receptionist see everyone — including
  // themselves — on /schedule). The floor roles KEEP it after #665 D1 (its
  // own-shifts contract is preserved server-side via the JWT-pinned ?self=1
  // narrowing now that they hold :read:all), and the accountant JOINS it
  // (#665 — it holds staff-schedule:read:self + time-off:request:self and
  // previously reached NO schedule surface at all: the dead-end the nav
  // invariant test now pins closed). The `prefixes` still gate (never widens
  // past the matrix): every listed role holds a staff-schedule:read grant.
  '/my-schedule': {
    prefixes: ['staff-schedule:read'],
    exactRoles: ['walker', 'groomer', 'kennel_tech', 'accountant'],
  },
  // #427 W1 — time clock. Every punch-holding role has
  // payroll.clock:write:self (scope-agnostic prefix admits :self AND :all
  // holders; accountant holds no payroll.clock grant and stays out), so
  // the punch surface is near-universal; the manager punch-records
  // surface uses the role-exclusive form (rules carry scope-less
  // prefixes only — the exactRoles gate mirrors the server's
  // payroll.clock:write:all owner/manager grant, and #435 role grants
  // pass via rolesCanAccess).
  '/timeclock': ['payroll.clock:write'],
  '/timeclock/manage': {
    prefixes: ['payroll.clock:write'],
    exactRoles: ['owner', 'manager'],
  },
  '/staff': ['rbac-admin:write'],
  '/audit': ['rbac-audit:read'],
  // AI Manager (Lane D2/D5) — gated on ai-manager:use so the surface is
  // reachable by exactly owner/manager/receptionist, mirroring the SERVER
  // enforcement (ai-manager:use:all on every /api/v2/admin/ai route). The
  // manifest's nav item names ai-manager.manage, but gating the PAGE on
  // `use` is the correct UX: a receptionist who can chat must see the nav.
  '/ai/manager': ['ai-manager:use'],
  // #277 — Conversations inbox. Gated on messaging:write — the same
  // permission the server issues for /api/v2/admin/messaging/*. Only
  // owner/manager/receptionist hold this grant; walker/groomer/kennel_tech
  // and accountant do NOT, so they see no nav item and get a RouteGuard
  // 403 shell on direct-URL deep-link. WS-F wires the real page component;
  // this entry makes the route gating correct before that ships.
  '/conversations': ['messaging:write'],
  // Sawyer 16 staff-messaging (E4) — #754, Team messages: the staff board + direct messages.
  // Every tenant role holds staff-messages:read:all (the accountant reads
  // only; posting is gated in the page on staff-messages:write:all). The
  // server enforces the same strings on /api/v2/admin/staff-messages/*.
  '/team-messages': ['staff-messages:read'],
  // Role-specific "My Work" home screens (#133/#134/#135/#290). These are
  // ROLE-EXCLUSIVE: reachable ONLY by the single persona that owns the
  // surface (plus platform_admin), NOT by every role that shares the broad
  // resource:action grant. A pure scope-agnostic prefix rule LEAKED them
  // into the desk-role menus — owner/manager hold bookings.*:read:all +
  // revenue:read:all, so the old `['bookings.walking:read', …]` /
  // `['revenue:read']` prefix rules matched owner/manager/receptionist too.
  // `exactRoles` pins each surface to its owner; the `prefixes` are still
  // required (defense-in-depth, never widens past the matrix). See
  // `RouteAccessExactRolesRule`.
  '/walker': {
    prefixes: ['bookings.walking:read', 'bookings.walking:write'],
    exactRoles: ['walker'],
  },
  // Sawyer 02 kennel-stopgap (A1-stopgap)
  // The kennel roster is ALSO the desk's
  // "Meds & feeding" list. The route admits exactly the server's admit set:
  // `requireKennelAccess` (packages/core/modules/staff-home/kennel-routes.ts)
  // and the `/api/v2/admin/kennel` rows in route-permission-map.ts let in
  // owner, manager, receptionist and kennel_tech (a daycare-boarding read AND
  // a vaccinations read). The persona NAV item stays pinned to kennel_tech
  // (`KENNEL_HOME_NAV_RULE` below), so #290's desk-menu leak stays closed.
  '/kennel': {
    prefixes: ['bookings.daycare-boarding:read'],
    exactRoles: ['kennel_tech', 'owner', 'manager', 'receptionist'],
  },
  '/groomer': {
    prefixes: ['bookings.grooming:read', 'bookings.grooming:write'],
    exactRoles: ['groomer'],
  },
  '/accountant': {
    prefixes: ['revenue:read'],
    exactRoles: ['accountant'],
  },
} as const satisfies Record<string, RouteAccessRule>;

/** The known guarded route keys (string-literal union, no `undefined`). */
export type GuardedRoute = keyof typeof ROUTE_ACCESS_RULES;

// Sawyer 02 kennel-stopgap (A1-stopgap)
/**
 * The "My Kennel" persona nav item's own rule. The
 * `/kennel` ROUTE admits the desk trio too (they open it as "Meds &
 * feeding"), but the sidebar item stays kennel_tech-only so a desk menu never
 * grows a persona entry (#290). A nav rule narrower than its route rule is
 * safe: the link is never offered to someone the route refuses. A unit test
 * enforces "never looser" for every role.
 */
export const KENNEL_HOME_NAV_RULE = {
  prefixes: ['bookings.daycare-boarding:read'],
  exactRoles: ['kennel_tech'],
} as const satisfies RouteAccessExactRolesRule;

/**
 * The order sections render in the sidebar. AppShell walks this list and,
 * for each section, renders only the items the current role can access
 * (empty sections render nothing — no orphan header). Keep this in sync
 * with the `group` values on `TENANT_NAV` and the `admin.nav.section.*`
 * i18n keys.
 */
export const NAV_SECTION_ORDER = [
  'admin.nav.section.overview',
  'admin.nav.section.operations',
  'admin.nav.section.people',
  'admin.nav.section.money',
  'admin.nav.section.assistant',
  'admin.nav.section.mywork',
  'admin.nav.section.admin',
] as const;

/** The set of valid section i18n keys (for the test tripwire). */
export type NavSection = (typeof NAV_SECTION_ORDER)[number];

/**
 * Icon key per nav item (spec C4, 2026-07-09 shell wave). The union lives
 * HERE (not in the icons module) so this file stays dependency-free for
 * node-side tests; `components/icons.tsx` maps each name to its 18px
 * stroke glyph via an exhaustive `Record<IconName, …>` — a nav item with
 * a name missing there is a compile error, never a blank slot.
 */
export type NavIconName =
  | 'dashboard'
  | 'frontDesk'
  | 'bookings'
  | 'schedule'
  | 'dayList'
  | 'lodging'
  | 'mySchedule'
  | 'timeclock'
  | 'timeclockManage'
  | 'incidents'
  | 'compliance'
  | 'vaxInbox'
  | 'customers'
  | 'staff'
  | 'payments'
  | 'loyalty'
  | 'pricingRules'
  | 'reports'
  | 'timesheets'
  | 'aiManager'
  | 'conversations'
  // Sawyer 16 staff-messaging (E4)
  | 'teamMessages'
  | 'walker'
  | 'kennel'
  // Sawyer 08 meds-feeding (A2) — the med pass: a pill.
  | 'medPass'
  | 'groomer'
  | 'accountant'
  | 'audit'
  | 'settings'
  | 'tenants'
  | 'tenantNew'
  | 'intakes';

/**
 * Surfaces whose existence depends on server-side PROVISIONING, not on the
 * caller's role.
 *
 * A role gate answers "may this person use it". This answers "does it exist
 * here yet". #551 needed the second: the vaccination inbox is useless — and
 * actively misleading — until Johnny provisions the tenant's inbound
 * mailbox (MX + Resend inbound route + the two server env vars). The ingest
 * half already failed closed on that config; the UI half had no gate at
 * all, and frontends auto-deploy on merge while the server does not, so
 * merging shipped a "Vaccination inbox" nav item to every existing tenant
 * pointing at an address that did not exist.
 *
 * Availability is fetched from the server (`GET /api/admin/vax-intake/
 * config`), never hard-coded here: the flag and the webhook's fail-closed
 * check read the SAME configuration, so they cannot disagree.
 */
// Sawyer 16 staff-messaging (E4) — `staffMessages`: the server has the Team messages routes
// (GET /api/v2/admin/staff-messages/unread answers 200). Frontends deploy on
// merge, the server by hand; until then the surface stays dark.
export type FeatureKey = 'vaxInbox' | 'ownerSettings' | 'staffMessages';

/**
 * Which provisioning-gated features this facility actually has. Absent /
 * unknown ⇒ NOT available (fail closed) — a surface must prove it exists
 * before it renders, never the other way round.
 */
export type FeatureAvailability = Readonly<Partial<Record<FeatureKey, boolean>>>;

/** Shared shape for a rendered nav item (tenant + platform). */
export type NavItem = {
  readonly to: string;
  readonly labelKey: string;
  readonly icon: NavIconName;
  /**
   * Optional provisioning gate. When set, the item renders only if
   * `features[feature] === true` AND the role gate passes. Most items have
   * no `feature` — they exist for every tenant.
   */
  readonly feature?: FeatureKey;
  /**
   * English fallback used when `labelKey` has no entry in the shared i18n
   * bundle yet (the bundle lives in `packages/core/i18n`, which this wave
   * does not touch). `navLabel(t, item)` prefers the translation and falls
   * back to this string — a user must never see a raw key like
   * `admin.nav.mySchedule` (Design Philosophy anti-pattern: internal
   * identifiers shown raw). Remove the fallback once the key is translated.
   */
  readonly labelDefault?: string;
};

/**
 * Resolve a nav item's visible label. Prefers the i18n bundle, falls back to
 * the item's English `labelDefault`, and only as a last resort returns the
 * key (which would be a bug worth seeing in dev, not in prod copy).
 */
export function navLabel(
  translate: (key: string, opts?: { defaultValue?: string }) => string,
  item: { readonly labelKey: string; readonly labelDefault?: string },
): string {
  return translate(item.labelKey, {
    defaultValue: item.labelDefault ?? item.labelKey,
  });
}

/**
 * Platform-admin nav (Ferro/IAID operator console). Rendered only when
 * `staff.role === 'platform_admin'` — the gate stays in AppShell, matching
 * `PlatformAdminRoute`. Per ADR-2026-04-28, platform admins live in a
 * control plane separate from any tenant. Moved here from AppShell (spec
 * C4) so nav metadata — labels, icons, `navLabelKeyForPath` — has one
 * home.
 */
export const PLATFORM_NAV: ReadonlyArray<NavItem> = [
  { to: '/platform/tenants', labelKey: 'admin.nav.tenants', icon: 'tenants' },
  {
    to: '/platform/tenants/new',
    labelKey: 'admin.nav.newTenant',
    icon: 'tenantNew',
  },
  { to: '/platform/intake', labelKey: 'admin.nav.intakes', icon: 'intakes' },
];

/**
 * The nav items rendered in `AppShell` and the access rule each needs.
 * Ordered the way they should appear in the sidebar. Label keys are the
 * existing i18n keys — unchanged so translations don't break. Each item
 * also carries a `group` (an `admin.nav.section.*` i18n key) so AppShell
 * can render labelled sections; the role-filter (`roleCanAccess`) is
 * applied per-item exactly as before — grouping is purely presentational.
 * The `icon` key feeds the sidebar/rail/drawer glyphs (spec C4).
 */
export const TENANT_NAV: ReadonlyArray<
  NavItem & {
    readonly rule: RouteAccessRule;
    readonly group: NavSection;
  }
> = [
  {
    to: '/',
    labelKey: 'admin.nav.dashboard',
    icon: 'dashboard',
    rule: ROUTE_ACCESS_RULES['/'],
    group: 'admin.nav.section.overview',
  },
  // Sawyer 16 staff-messaging (E4) — #754, Team messages, in Overview: the one section every
  // role sees, so floor staff find it at the top of the drawer. Gated on
  // the server having the routes (feature) as well as the role.
  {
    to: '/team-messages',
    labelKey: 'admin.nav.teamMessages',
    labelDefault: 'Team messages',
    icon: 'teamMessages',
    feature: 'staffMessages',
    rule: ROUTE_ACCESS_RULES['/team-messages'],
    group: 'admin.nav.section.overview',
  },
  // #548 — Front-Desk simple mode. First item in Operations so the daily
  // check-in surface is the top of the floor staff's world. labelDefault
  // carries the English copy until the i18n key lands (same pattern as
  // /my-schedule and /timesheets).
  {
    to: '/front-desk',
    labelKey: 'admin.nav.frontDesk',
    labelDefault: 'Front Desk',
    icon: 'frontDesk',
    rule: ROUTE_ACCESS_RULES['/front-desk'],
    group: 'admin.nav.section.operations',
  },
  {
    to: '/bookings',
    labelKey: 'admin.nav.bookings',
    icon: 'bookings',
    rule: ROUTE_ACCESS_RULES['/bookings'],
    group: 'admin.nav.section.operations',
  },
  {
    to: '/schedule',
    labelKey: 'admin.nav.schedule',
    icon: 'schedule',
    rule: ROUTE_ACCESS_RULES['/schedule'],
    group: 'admin.nav.section.operations',
  },
  // Wave 5 (#940 Part A) — Ted: "Jorge looks at it when he comes in each
  // morning to see what grooming he has scheduled." Every role reaches it
  // (dashboard:read); it is the day's list, not a fifth surface.
  {
    to: '/calendar/day',
    labelKey: 'admin.nav.dayList',
    labelDefault: 'Day list',
    icon: 'dayList',
    rule: ROUTE_ACCESS_RULES['/calendar/day'],
    group: 'admin.nav.section.operations',
  },
  // Wave 6 (#939 3b) — Ted's Lodging Calendar: beds, stays, who has no bed.
  // Desk trio only (the server's gate); floor roles keep their own surfaces.
  {
    to: '/calendar/lodging',
    labelKey: 'admin.nav.lodging',
    labelDefault: 'Lodging',
    icon: 'lodging',
    rule: ROUTE_ACCESS_RULES['/calendar/lodging'],
    group: 'admin.nav.section.operations',
  },
  // #427 W1 — manager punch records (owner/manager via the exact
  // payroll.clock:write:all pin). The universal staff punch surface
  // (/timeclock) lives in the My Work section below.
  {
    to: '/timeclock/manage',
    labelKey: 'admin.nav.timeclockManage',
    icon: 'timeclockManage',
    rule: ROUTE_ACCESS_RULES['/timeclock/manage'],
    group: 'admin.nav.section.people',
  },
  {
    to: '/incidents',
    labelKey: 'admin.nav.incidents',
    icon: 'incidents',
    rule: ROUTE_ACCESS_RULES['/incidents'],
    group: 'admin.nav.section.operations',
  },
  {
    to: '/compliance',
    labelKey: 'admin.nav.compliance',
    icon: 'compliance',
    rule: ROUTE_ACCESS_RULES['/compliance'],
    group: 'admin.nav.section.operations',
  },
  // #551 — the ONE provisioning-gated nav item. `feature: 'vaxInbox'` means
  // the desk trio still only see it once the facility's inbound mailbox
  // exists; until then there is no item, no route, and no copy telling a
  // receptionist to send vets to an address that isn't live. See
  // `FeatureKey`.
  {
    to: '/vax-inbox',
    labelKey: 'admin.nav.vaxInbox',
    labelDefault: 'Vaccination inbox',
    icon: 'vaxInbox',
    feature: 'vaxInbox',
    rule: ROUTE_ACCESS_RULES['/vax-inbox'],
    group: 'admin.nav.section.operations',
  },
  {
    to: '/customers',
    labelKey: 'admin.nav.customers',
    icon: 'customers',
    rule: ROUTE_ACCESS_RULES['/customers'],
    group: 'admin.nav.section.people',
  },
  {
    to: '/staff',
    labelKey: 'admin.nav.staff',
    icon: 'staff',
    rule: ROUTE_ACCESS_RULES['/staff'],
    group: 'admin.nav.section.people',
  },
  // R1 (#252) — /payments was a guarded route with NO nav item anywhere;
  // staff had to hand-type the URL to reach the open-invoices queue /
  // settle / gateway-credentials console. Gated by the exact rule object
  // the route guard uses (#257: payments-ops:read — never a forked or
  // looser gate), so no role sees an item whose console then 403s.
  {
    to: '/payments',
    labelKey: 'admin.nav.payments',
    icon: 'payments',
    rule: ROUTE_ACCESS_RULES['/payments'],
    group: 'admin.nav.section.money',
  },
  // Spec C5 (2026-07-09 shell wave) — /loyalty was a guarded route with
  // NO nav item (same dead-end class as #252's /payments): staff had to
  // hand-type the URL to reach the rewards/redemption surface. Gated by
  // the exact rule object the route guard uses (customers:read — loyalty
  // is a customer-reward surface), so walker/groomer/kennel_tech never
  // see an item the server would 403.
  {
    to: '/loyalty',
    labelKey: 'admin.nav.loyalty',
    icon: 'loyalty',
    rule: ROUTE_ACCESS_RULES['/loyalty'],
    group: 'admin.nav.section.money',
  },
  // #814 — owner-managed automatic discounts/surcharges. labelDefault
  // carries the English copy until the i18n key lands (the /front-desk
  // pattern). Owner-only via the pricing-write gate above.
  {
    to: '/pricing-rules',
    labelKey: 'admin.nav.pricingRules',
    labelDefault: 'Pricing rules',
    icon: 'pricingRules',
    rule: ROUTE_ACCESS_RULES['/pricing-rules'],
    group: 'admin.nav.section.money',
  },
  {
    to: '/reports',
    labelKey: 'admin.nav.reports',
    icon: 'reports',
    rule: ROUTE_ACCESS_RULES['/reports'],
    group: 'admin.nav.section.money',
  },
  // #427 W3 — Timesheets & Payroll export. Money section, gated by the exact
  // route rule (payroll:read:all → owner/manager/accountant). labelDefault
  // carries the English copy until the i18n key lands (same pattern as
  // /my-schedule), so a user never sees the raw key.
  {
    to: '/timesheets',
    labelKey: 'admin.nav.timesheets',
    labelDefault: 'Timesheets',
    icon: 'timesheets',
    rule: ROUTE_ACCESS_RULES['/timesheets'],
    group: 'admin.nav.section.money',
  },
  {
    to: '/ai/manager',
    labelKey: 'admin.nav.aiManager',
    icon: 'aiManager',
    rule: ROUTE_ACCESS_RULES['/ai/manager'],
    group: 'admin.nav.section.assistant',
  },
  // #277 — Conversations inbox nav item. The real staff inbox component
  // (Conversations.tsx, wired to /api/v2/admin/messaging) shipped, so the nav
  // entry is now live. Gated on messaging:write (ROUTE_ACCESS_RULES['/conversations']),
  // so only owner/manager/receptionist see it. Server-side tenant isolation
  // enforced via resolveStaffTenant + customer-relation scoping (#347 IDOR fix).
  {
    to: '/conversations',
    labelKey: 'admin.nav.conversations',
    icon: 'conversations',
    rule: ROUTE_ACCESS_RULES['/conversations'],
    group: 'admin.nav.section.operations',
  },
  // Role-specific home screens — only visible to the matching persona.
  // #427 W1 — universal punch surface: every role holds
  // payroll.clock:write:self, so this appears in everyone's My Work.
  {
    to: '/timeclock',
    labelKey: 'admin.nav.timeclock',
    icon: 'timeclock',
    rule: ROUTE_ACCESS_RULES['/timeclock'],
    group: 'admin.nav.section.mywork',
  },
  // #427 W2 (#400) — the floor staff's own week. Sits in My Work next to the
  // punch surface: walker / groomer / kennel_tech only (the desk roles see
  // everyone on /schedule, which already includes themselves).
  {
    to: '/my-schedule',
    labelKey: 'admin.nav.mySchedule',
    labelDefault: 'My Schedule',
    icon: 'mySchedule',
    rule: ROUTE_ACCESS_RULES['/my-schedule'],
    group: 'admin.nav.section.mywork',
  },
  // Sawyer 08 meds-feeding (A2) — the floor's med pass, beside the floor's
  // own week. Gated by the exact route rule (pets.notes:write): the six care
  // roles see it, the accountant never does.
  {
    to: '/med-pass',
    labelKey: 'admin.nav.medPass',
    labelDefault: 'Med pass',
    icon: 'medPass',
    rule: ROUTE_ACCESS_RULES['/med-pass'],
    group: 'admin.nav.section.mywork',
  },
  {
    to: '/walker',
    labelKey: 'admin.nav.walkerHome',
    icon: 'walker',
    rule: ROUTE_ACCESS_RULES['/walker'],
    group: 'admin.nav.section.mywork',
  },
  {
    to: '/kennel',
    labelKey: 'admin.nav.kennelHome',
    icon: 'kennel',
    rule: KENNEL_HOME_NAV_RULE, // Sawyer 02 kennel-stopgap (A1-stopgap): kennel_tech-only, narrower than the route
    group: 'admin.nav.section.mywork',
  },
  {
    to: '/groomer',
    labelKey: 'admin.nav.groomerHome',
    icon: 'groomer',
    rule: ROUTE_ACCESS_RULES['/groomer'],
    group: 'admin.nav.section.mywork',
  },
  {
    to: '/accountant',
    labelKey: 'admin.nav.accountantHome',
    icon: 'accountant',
    rule: ROUTE_ACCESS_RULES['/accountant'],
    group: 'admin.nav.section.mywork',
  },
  // #933 — Owner Settings. Sits in the Admin section beside the audit log;
  // gated by the exact rule the route guard uses. labelDefault carries the
  // English copy until the i18n key lands in every bundle (the /front-desk
  // pattern).
  {
    to: '/settings',
    labelKey: 'admin.nav.settings',
    labelDefault: 'Settings',
    icon: 'settings',
    // Provisioning-gated on the SERVER carrying the route (#551's lesson,
    // applied to the deploy split): frontends deploy on merge, the server
    // by hand, so without this gate a merged Settings page would put an
    // item in Ted's menu that lands on a 403 until the server is deployed.
    feature: 'ownerSettings',
    rule: ROUTE_ACCESS_RULES['/settings'],
    group: 'admin.nav.section.admin',
  },
  // Audit log — folded into TENANT_NAV (was special-cased in AppShell).
  // Gated on rbac-audit:read (Owner + Manager); the route guard enforces
  // the same on /api/admin/rbac-audit. Lives in the Admin section.
  {
    to: '/audit',
    labelKey: 'admin.nav.auditLog',
    icon: 'audit',
    rule: ROUTE_ACCESS_RULES['/audit'],
    group: 'admin.nav.section.admin',
  },
];

/**
 * Splits a `resource:action:scope` permission into its
 * `resource:action` prefix (everything before the last `:`). Returns
 * `null` for a string without the expected shape so a malformed matrix
 * entry fails closed rather than matching everything.
 */
function permissionPrefix(permission: string): string | null {
  const lastColon = permission.lastIndexOf(':');
  if (lastColon <= 0 || lastColon === permission.length - 1) return null;
  return permission.slice(0, lastColon);
}

function isRoleSlug(value: string | null | undefined): value is RoleSlug {
  return (
    value === 'owner' ||
    value === 'manager' ||
    value === 'receptionist' ||
    value === 'walker' ||
    value === 'groomer' ||
    value === 'kennel_tech' ||
    value === 'accountant'
  );
}

/**
 * Can this role reach the surface guarded by `rule`?
 *
 * For a plain (scope-agnostic) rule: `true` if the role holds ANY
 * permission whose `resource:action` prefix is in the rule (scope
 * ignored — see file header).
 *
 * For a role-EXCLUSIVE rule (#290): `true` only if the role is listed in
 * `exactRoles` AND also holds one of the rule's prefix grants. The
 * `exactRoles` membership is the gate that stops persona "My Work"
 * screens leaking into desk-role menus; the prefix check is retained so
 * the gate can never widen past the matrix.
 *
 * - `platform_admin` → always `true` (control-plane bypass,
 *   ADR-2026-04-28; consistent with `roleHasPermission`). This bypass
 *   applies to `exactRoles` rules too — the operator console sees every
 *   tenant surface.
 * - Unknown / null / undefined role → `false` (fail closed).
 * - An empty rule (no prefixes) → `false` (a route with no rule is not
 *   reachable; surfaces a wiring mistake instead of silently opening).
 * - A role-exclusive rule with an empty `exactRoles` → `false` for every
 *   tenant role (only `platform_admin` passes) — same fail-closed posture.
 */
export function roleCanAccess(
  role: string | null | undefined,
  rule: RouteAccessRule,
): boolean {
  if (role === PLATFORM_ADMIN_ROLE) return true;
  if (!isRoleSlug(role)) return false;

  // Role-exclusive surfaces: the owning role(s) gate first. A role not in
  // `exactRoles` is denied even if it holds the broad prefix grant — this
  // is exactly the leak (#290) the exactRoles form exists to close.
  if (isExactRolesRule(rule)) {
    if (!rule.exactRoles.includes(role)) return false;
  }

  const prefixes = rulePrefixes(rule);
  if (prefixes.length === 0) return false;
  const allowed = new Set(prefixes);
  for (const permission of ROLE_PERMISSIONS[role]) {
    const prefix = permissionPrefix(permission);
    if (prefix !== null && allowed.has(prefix)) return true;
  }
  return false;
}

/**
 * #428 — multi-role variant of `roleCanAccess`: can ANY of these
 * effective roles (primary + additive grants, from
 * `effectiveRolesOf(staff)`) reach the surface? Each role is evaluated
 * through `roleCanAccess` unchanged, so:
 *
 *  - the `exactRoles` persona pins (#290) match when ANY effective role
 *    matches — a groomer with a receptionist grant sees BOTH /groomer
 *    and the desk surfaces (customers, payments desk, AIM). That is the
 *    entire point of the feature (the Jorge case): #290 stopped a DESK
 *    role's broad grants leaking into persona menus, and that stays
 *    true — owner/manager still don't match — but an explicit role
 *    grant IS membership in the role.
 *  - a single-element list is byte-identical to `roleCanAccess`
 *    (the no-grants regression guarantee).
 *  - empty / null / undefined ⇒ false (fail closed).
 */
export function rolesCanAccess(
  roles: ReadonlyArray<string> | null | undefined,
  rule: RouteAccessRule,
): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some((role) => roleCanAccess(role, rule));
}

/**
 * Which provisioning-gated features could this staffer EVER see, on role
 * alone? Empty means the availability probe would change nothing for them,
 * so the shell skips the network call entirely.
 *
 * This is not just an optimization. The probe is the admin shell's only
 * mount-time fetch, and firing it for a staffer who fails the role gate on
 * every gated surface spends a request per page load to compute an answer
 * that cannot affect the UI. A `platform_admin` is the clearest case: they
 * render PLATFORM_NAV, which has no gated item at all.
 */
export function gatedFeaturesVisibleTo(
  roles: ReadonlyArray<string> | null | undefined,
): ReadonlyArray<FeatureKey> {
  // Mirror the shell's own either/or: AppShell renders PLATFORM_NAV for a
  // platform_admin and TENANT_NAV for everyone else — never both. Walking
  // the union would claim a platform operator can see a tenant's
  // vaccination inbox, which the sidebar never offers them. It matters
  // beyond tidiness: platform_admin satisfies every role rule, so the
  // union form probes on every platform-console page load for a tenant
  // surface they have no tenant for (GET /config answers "not enabled" for
  // a null tenant anyway).
  //
  // PLATFORM_NAV items carry no access rule — the whole console is behind
  // one platform_admin check — so there is nothing to evaluate on that
  // branch. A guard test asserts PLATFORM_NAV stays free of `feature`, which
  // is what makes this early return complete rather than merely convenient.
  if ((roles ?? []).includes('platform_admin')) return [];
  const out = new Set<FeatureKey>();
  for (const item of TENANT_NAV) {
    if (item.feature && rolesCanAccess(roles, item.rule)) out.add(item.feature);
  }
  return [...out];
}

/**
 * Is this feature provisioned for this facility? Fail-closed on
 * `undefined` — which covers "the probe hasn't answered yet" and "the
 * probe failed" as well as "not provisioned". A surface that has not
 * proven it exists does not render.
 */
export function featureIsAvailable(
  feature: FeatureKey | undefined,
  features: FeatureAvailability | null | undefined,
): boolean {
  if (feature === undefined) return true; // ungated — exists for every tenant
  return features?.[feature] === true;
}

/**
 * The full nav-visibility decision for one item: BOTH gates, in the order
 * they mean things.
 *
 *   provisioning — does this surface exist in this facility at all?
 *   role         — may this person use it?
 *
 * They are independent and both required. Keeping the pair in one pure
 * function is what stops the next nav-item author remembering one and not
 * the other: `AppShell` calls exactly this, and the guard test drives
 * exactly this.
 */
export function navItemIsVisible(
  item: { readonly rule: RouteAccessRule; readonly feature?: FeatureKey },
  roles: ReadonlyArray<string> | null | undefined,
  features: FeatureAvailability | null | undefined,
): boolean {
  if (!featureIsAvailable(item.feature, features)) return false;
  return rolesCanAccess(roles, item.rule);
}

/**
 * Resolves a concrete pathname (which may carry params, e.g.
 * `/customers/abc-123`) to its access rule by longest-prefix match
 * against `ROUTE_ACCESS_RULES`. Returns `null` when no guarded route
 * matches — callers treat `null` as "not a gated route" (e.g. the
 * catch-all 404, `/login`) and let it through to its own handler.
 */
export function accessRuleForPath(pathname: string): RouteAccessRule | null {
  const entries = Object.entries(ROUTE_ACCESS_RULES) as ReadonlyArray<
    readonly [GuardedRoute, RouteAccessRule]
  >;
  let best: { key: string; rule: RouteAccessRule } | null = null;
  for (const [key, rule] of entries) {
    // Exact match wins outright (covers '/', and any path that is
    // itself a key).
    if (pathname === key) return rule;
    if (key === '/') continue; // '/' would prefix-match everything.
    if (
      pathname.startsWith(`${key}/`) &&
      (best === null || key.length > best.key.length)
    ) {
      best = { key, rule };
    }
  }
  return best?.rule ?? null;
}

/**
 * Convenience: can this role reach this concrete pathname? `true` for
 * paths that aren't gated routes (let the router's own 404 handle them)
 * — this helper answers "is the user *blocked* from a gated route",
 * not "does this path exist".
 */
export function roleCanAccessPath(
  role: string | null | undefined,
  pathname: string,
): boolean {
  const rule = accessRuleForPath(pathname);
  if (rule === null) return true;
  return roleCanAccess(role, rule);
}

/**
 * #428 — multi-role variant of `roleCanAccessPath`: true when ANY
 * effective role can reach the (gated) pathname; non-gated paths pass
 * through exactly as before.
 */
export function rolesCanAccessPath(
  roles: ReadonlyArray<string> | null | undefined,
  pathname: string,
): boolean {
  const rule = accessRuleForPath(pathname);
  if (rule === null) return true;
  return rolesCanAccess(roles, rule);
}

/**
 * Role-aware landing (spec D3, 2026-07-09 wave): the persona roles live
 * in their "My Work" surface, not the desk dashboard — a groomer signing
 * in at 6:45am should see their queue, not facility-wide chrome. Maps a
 * role slug to its home route, or null for the desk roles
 * (owner/manager/receptionist → '/') and unknown/absent roles (fail to
 * the default; ProtectedRoute still guards auth).
 *
 * Used by Login (post-login bounce) and the '/' route default. Deep
 * links win over this default: a groomer following /schedule?day=…
 * lands on /schedule, not /groomer (state.from is preserved).
 *
 * #428 — deliberately PRIMARY-role-driven: additive role grants widen
 * which surfaces an account can reach (see `rolesCanAccess`), but the
 * landing screen stays whatever the primary role owns. A groomer with a
 * receptionist grant still lands on /groomer.
 */
export function personaHomeRoute(
  role: string | null | undefined,
): '/walker' | '/kennel' | '/groomer' | '/accountant' | null {
  switch (role) {
    case 'walker':
      return '/walker';
    case 'kennel_tech':
      return '/kennel';
    case 'groomer':
      return '/groomer';
    case 'accountant':
      return '/accountant';
    default:
      return null;
  }
}

/**
 * The topbar's current-page title (spec C1): resolves a concrete pathname
 * to the i18n label key of the nav item that owns it, by exact match
 * first and longest-prefix match otherwise (`/customers/abc-123` →
 * `admin.nav.customers`, `/platform/tenants/new` → `admin.nav.newTenant`
 * — exact beats its `/platform/tenants` prefix). Searches TENANT_NAV +
 * PLATFORM_NAV so one helper covers both planes. Returns `null` when no
 * nav item matches (the 404 catch-all, un-navigated routes) — callers
 * fall back to the brand name.
 */
export function navLabelKeyForPath(pathname: string): string | null {
  return navItemForPath(pathname)?.labelKey ?? null;
}

/**
 * The nav item that owns a pathname (exact match first, longest-prefix
 * otherwise). Exposed so the topbar can resolve BOTH the label key and its
 * `labelDefault` — see `navLabel`.
 */
export function navItemForPath(pathname: string): NavItem | null {
  const items: ReadonlyArray<NavItem> = [...TENANT_NAV, ...PLATFORM_NAV];
  let best: NavItem | null = null;
  for (const item of items) {
    if (pathname === item.to) return item;
    if (item.to === '/') continue; // '/' would prefix-match everything.
    if (
      pathname.startsWith(`${item.to}/`) &&
      (best === null || item.to.length > best.to.length)
    ) {
      best = item;
    }
  }
  return best;
}
